import { useEffect, useState } from 'react';
import { setQueryParamsFromObject } from './useStateUrl';

export function useStateLocalStorageAndUrl<T extends Record<string, any>>(
  key: string,
  initialState: T,
  serialize?: { [K in keyof T]?: (value: T[K]) => string | null },
  deserialize?: { [K in keyof T]?: (value: string) => T[K] }
) {
  const [state, _setState] = useState(initializeStateFromUrlOrLocalStorage());

  useEffect(() => {
    setState(initializeStateFromUrlOrLocalStorage());
  }, []);

  function setState(newState: T) {
    console.log('Setting state', newState);
    _setState(newState);

    if (newState) {
      window.localStorage.setItem(key, JSON.stringify(newState));
      const transformedState: any = { ...newState };
      if (serialize) {
        for (const k in transformedState) {
          if (serialize[k]) {
            transformedState[k] = serialize[k]!(transformedState[k]);
          }
        }
      }
      setQueryParamsFromObject(transformedState);
    } else {
      window.localStorage.removeItem(key);
      setQueryParamsFromObject({});
    }
  }

  function setField<K extends keyof T>(key: K, value: T[K]) {
    const newState = { ...state, [key]: value };
    setState(newState as T);
  }

  function initializeStateFromUrlOrLocalStorage() {
    const url = new URL(window.location.href);
    const queryParams = new URLSearchParams(url.search);
    console.log('Initializing state from URL', queryParams.toString());
    if (queryParams.size === 0) {
      console.log('No query params found, loading from local storage');
      // No query params, load from local storage
      const item = window.localStorage.getItem(key);
      if (item) {
        try {
          const parsed = JSON.parse(item);
          return { ...initialState, ...parsed };
        } catch (e) {
          return initialState;
        }
      }
    } else {
      console.log('Query params found, loading from URL');
      // Load from URL query params
      const newState: T = { ...initialState };
      for (const key in initialState) {
        const value = queryParams.get(key);
        if (value !== null) {
          if (deserialize && deserialize[key]) {
            const deserialized = deserialize[key]!(value);
            newState[key] = deserialized;
            continue;
          }
          const initialType = typeof initialState[key];
          if (initialType === 'number') {
            newState[key] = Number(value) as any;
          } else if (initialType === 'boolean') {
            newState[key] = (value === 'true') as any;
          } else {
            newState[key] = value as any;
          }
        }
      }
      return newState;
    }
  }

  return [state as T, setState, setField] as const;
}
